/**
 * Hook personnalisé pour la gestion des calculs AEP
 */

import { useState, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import {
  DEFAULTS,
  DEBITS_LMIN,
  DEBITS_M3H,
  PRESSIONS_AMONT,
  MODES_DUREE,
  PRESSION_AVAL_MIN
} from '../lib/aep-constants';
import {
  calculerPressionExploitable,
  calculerResultatsComplets,
  calculerPuissanceTheorique,
  calculerPuissanceUtile,
  calculerEnergie,
  calculerRevenus,
  formatNumber,
  formatPrice
} from '../lib/aep-calculations';

export const useAEPCalculator = () => {
  // États des paramètres
  const [pressionAmont, setPressionAmont] = useState(DEFAULTS.pressionAmont);
  const [unite, setUnite] = useState(DEFAULTS.unite);
  const [debit, setDebit] = useState(DEFAULTS.debit);
  const [rendement, setRendement] = useState(DEFAULTS.rendement);
  const [mode, setMode] = useState(DEFAULTS.mode);
  
  // États UI
  const [showTable, setShowTable] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [copied, setCopied] = useState(false);

  // Débits disponibles selon l'unité
  const debitsDisponibles = useMemo(() => {
    return unite === 'lmin' ? DEBITS_LMIN : DEBITS_M3H;
  }, [unite]);

  // Pression exploitable calculée
  const pressionExploitable = useMemo(() => {
    return calculerPressionExploitable(pressionAmont);
  }, [pressionAmont]);

  // Durée en heures selon le mode
  const dureeHeures = useMemo(() => {
    return MODES_DUREE[mode].heures;
  }, [mode]);

  // Ajuster le débit si nécessaire lors du changement d'unité
  const handleUniteChange = useCallback((newUnite) => {
    setUnite(newUnite);
    const newDebits = newUnite === 'lmin' ? DEBITS_LMIN : DEBITS_M3H;
    setDebit(prevDebit => {
      if (!newDebits.includes(prevDebit)) {
        return newDebits[2]; // Valeur médiane
      }
      return prevDebit;
    });
  }, []);

  // Calculs des résultats principaux
  const resultats = useMemo(() => {
    return calculerResultatsComplets({
      pressionAmont,
      debit,
      unite,
      rendement,
      dureeHeures
    });
  }, [pressionAmont, debit, unite, rendement, dureeHeures]);

  // Génération du tableau de combinaisons
  const tableauCombinaisons = useMemo(() => {
    const combinaisons = [];
    const unites = ['lmin', 'm3h'];
    
    unites.forEach(u => {
      const debits = u === 'lmin' ? DEBITS_LMIN : DEBITS_M3H;
      
      PRESSIONS_AMONT.forEach(pAmont => {
        const pExploitable = calculerPressionExploitable(pAmont);
        
        debits.forEach(d => {
          const puissanceTheoriqueW = calculerPuissanceTheorique(pExploitable, d, u);
          const puissanceTheoriqueKW = puissanceTheoriqueW / 1000;
          const puissanceUtileW = calculerPuissanceUtile(puissanceTheoriqueW, rendement);
          const puissanceUtileKW = puissanceUtileW / 1000;
          const energieTheoriqueKWh = calculerEnergie(puissanceTheoriqueKW, dureeHeures);
          const energieUtileKWh = calculerEnergie(puissanceUtileKW, dureeHeures);
          const revenus = calculerRevenus(energieUtileKWh);

          combinaisons.push({
            pressionAmont: pAmont,
            pressionAval: PRESSION_AVAL_MIN,
            pressionExploitable: pExploitable,
            debit: d,
            unite: u,
            uniteLabel: u === 'lmin' ? 'L/min' : 'm³/h',
            rendement,
            mode: MODES_DUREE[mode].label,
            puissanceTheoriqueW,
            puissanceTheoriqueKW,
            puissanceUtileW,
            puissanceUtileKW,
            energieTheoriqueKWh,
            energieUtileKWh,
            revenusVenteEDF: revenus.venteEDF,
            revenusAutoconsommation: revenus.autoconsommation,
            isSelected: pAmont === pressionAmont && d === debit && u === unite,
            isNonExploitable: pExploitable === 0
          });
        });
      });
    });

    return combinaisons;
  }, [rendement, mode, dureeHeures, pressionAmont, debit, unite]);

  // Filtrage du tableau
  const tableauFiltre = useMemo(() => {
    if (!searchTerm) return tableauCombinaisons;
    
    const term = searchTerm.toLowerCase();
    return tableauCombinaisons.filter(row => 
      row.pressionAmont.toString().includes(term) ||
      row.debit.toString().includes(term) ||
      row.uniteLabel.toLowerCase().includes(term) ||
      row.mode.toLowerCase().includes(term)
    );
  }, [tableauCombinaisons, searchTerm]);

  // Tri du tableau
  const tableauTrie = useMemo(() => {
    if (!sortConfig.key) return tableauFiltre;
    
    return [...tableauFiltre].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [tableauFiltre, sortConfig]);

  // Fonction de tri
  const handleSort = useCallback((key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Réinitialiser aux valeurs par défaut
  const handleReset = useCallback(() => {
    setPressionAmont(DEFAULTS.pressionAmont);
    setUnite(DEFAULTS.unite);
    setDebit(DEFAULTS.debit);
    setRendement(DEFAULTS.rendement);
    setMode(DEFAULTS.mode);
    setSearchTerm('');
    setSortConfig({ key: null, direction: 'asc' });
    toast.success('Paramètres réinitialisés');
  }, []);

  // Copier les résultats
  const handleCopy = useCallback(() => {
    const uniteLabel = unite === 'lmin' ? 'L/min' : 'm³/h';
    const text = `
Calculatrice AEP - ELECTRON INDUSTRIE
======================================
Paramètres:
- Pression amont: ${pressionAmont} bar
- Pression aval minimale: ${PRESSION_AVAL_MIN} bar
- Pression exploitable: ${pressionExploitable} bar
- Débit: ${debit} ${uniteLabel}
- Rendement: ${rendement}%
- Mode: ${MODES_DUREE[mode].label} (${dureeHeures}h)

Résultats:
- Puissance théorique: ${formatNumber(resultats.puissanceTheoriqueW)} W (${formatNumber(resultats.puissanceTheoriqueKW)} kW)
- Puissance utile: ${formatNumber(resultats.puissanceUtileW)} W (${formatNumber(resultats.puissanceUtileKW)} kW)
- Énergie théorique: ${formatNumber(resultats.energieTheoriqueKWh)} kWh
- Énergie utile: ${formatNumber(resultats.energieUtileKWh)} kWh

Revenus potentiels (période: ${MODES_DUREE[mode].label}):
- Vente EDF (0,04 €/kWh): ${formatPrice(resultats.revenusVenteEDF)}
- Autoconsommation (0,19 €/kWh): ${formatPrice(resultats.revenusAutoconsommation)}
    `.trim();
    
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success('Résultats copiés dans le presse-papiers');
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      toast.error('Erreur lors de la copie');
    });
  }, [pressionAmont, pressionExploitable, debit, unite, rendement, mode, dureeHeures, resultats]);

  // Export CSV
  const handleExportCSV = useCallback(() => {
    const headers = [
      'Pression amont (bar)',
      'Pression aval min (bar)',
      'Pression exploitable (bar)',
      'Débit',
      'Unité',
      'Rendement (%)',
      'Mode',
      'Puissance théorique (W)',
      'Puissance théorique (kW)',
      'Puissance utile (W)',
      'Puissance utile (kW)',
      'Énergie théorique (kWh)',
      'Énergie utile (kWh)',
      'Revenus vente EDF (€)',
      'Revenus autoconsommation (€)'
    ];
    
    const rows = tableauTrie.map(row => [
      row.pressionAmont,
      row.pressionAval,
      row.pressionExploitable,
      row.debit,
      row.uniteLabel,
      row.rendement,
      row.mode,
      row.puissanceTheoriqueW.toFixed(2),
      row.puissanceTheoriqueKW.toFixed(4),
      row.puissanceUtileW.toFixed(2),
      row.puissanceUtileKW.toFixed(4),
      row.energieTheoriqueKWh.toFixed(2),
      row.energieUtileKWh.toFixed(2),
      row.revenusVenteEDF.toFixed(2),
      row.revenusAutoconsommation.toFixed(2)
    ]);
    
    const BOM = '\uFEFF';
    const csv = BOM + [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\r\n');
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'calculatrice_potentiel_aep.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Export CSV téléchargé');
  }, [tableauTrie]);

  return {
    // États
    pressionAmont,
    setPressionAmont,
    unite,
    debit,
    setDebit,
    rendement,
    setRendement,
    mode,
    setMode,
    showTable,
    setShowTable,
    searchTerm,
    setSearchTerm,
    sortConfig,
    copied,
    
    // Valeurs calculées
    pressionExploitable,
    debitsDisponibles,
    dureeHeures,
    resultats,
    tableauTrie,
    
    // Handlers
    handleUniteChange,
    handleSort,
    handleReset,
    handleCopy,
    handleExportCSV
  };
};

export default useAEPCalculator;
